import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Card, Flex, Input, Typography } from 'antd';

import dayjs from 'dayjs';

import { hasPermission } from '../../../helpers/user';

import { DATE_FORMAT } from '../../../constants/common';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';

import { useModal } from '../../../hooks/useModal';
import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { deleteGateway, fetchAdminGateway, updateGateway } from '../../../redux/actions/gateway';

import AppModal from '../../../components/AppModal';
import CancelSaveButtons from '../../../components/CancelSaveButtons';
import LoadingBar from '../../../components/LoadingBar';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../../components/action_notifier';
import AppForm from '../../../components/ui/AppForm';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';
import AppDropdown from '../../ui-components/AppDropdown';

const { Title } = Typography;

function EditGateway() {
  const dispatch = useDispatch();
  const history = useHistory();
  const formEditRef = useRef(null);
  const { id } = useParams();

  const { permissions } = useSelector(state => state.user);
  const { gateway, loading } = useSelector(state => state.gateway);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [archiveModalVisible, openArchiveModal, closeArchiveModal] = useModal();
  const [state, setState] = useState({
    editData: {},
    editing: false
  });

  useEffect(() => {
    getGateway();
  }, []);

  async function getGateway() {
    try {
      await dispatch(fetchAdminGateway(id)).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  const onCancelEdit = (value, field) => {
    setState(state => ({ ...state, editData: gateway, [field]: value }));
  };

  function enableEditing() {
    setState(state => ({
      ...state,
      editing: true,
      editData: { ...gateway }
    }));
  }

  const onChange = (value, field) => {
    setState(state => ({
      ...state,
      editData: {
        ...state.editData,
        [field]: value
      }
    }));
  };

  async function deleteGateways() {
    try {
      await dispatch(deleteGateway({ data: { ids: [gateway?.id] } })).unwrap();
      history.push('/administration/devices?tab=gateways');
      successToastHandler(messages.ARCHIVED);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    } finally {
      closeArchiveModal();
    }
  }

  async function saveGateway() {
    try {
      await dispatch(updateGateway({ id, data: { ...state.editData } }));

      setState(prev => ({ ...prev, editing: false }));
      setIsFormDirty(false);
    } catch (error) {
      errorToastHandler(messages.DEFAULT_ERROR);
    }
  }

  const actions = [
    {
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!hasPermission([PERMISSIONS.GATEWAYS.UPDATE_ALL], permissions)}
          onClick={enableEditing}>
          Edit Kraal Gateway
        </Button>
      )
    },
    {
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!hasPermission([PERMISSIONS.GATEWAYS.DELETE_ALL], permissions)}
          onClick={openArchiveModal}>
          Archive Kraal tag
        </Button>
      )
    }
  ];

  const data = [
    {
      label: 'Gateway ID',
      value: gateway?.id || '/',
      isVisible: true
    },
    {
      label: 'Farm ID',
      value: gateway?.farm_id || '/',
      isVisible: true
    },
    {
      label: 'Identifier',
      name: 'identifier',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'identifier')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Identifier') }]
    },
    {
      label: 'Gateway Name',
      name: 'name',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'name')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Gateway Name') }]
    },
    {
      label: 'Appeui',
      name: 'appeui',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'appeui')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Appeui') }]
    },
    {
      label: 'Gweui',
      name: 'gweui',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'gweui')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Gweui') }]
    },
    {
      label: 'ARN',
      name: 'arn',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'arn')} />,
      isVisible: true
    },
    {
      label: 'Version',
      name: 'version',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'version')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Version') }]
    },
    {
      label: 'Carrier Type',
      name: 'carrier_type',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'carrier_type')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Carrier Type') }]
    },
    {
      label: 'Connection Speed',
      value: gateway?.connection_speed || '/',
      isVisible: true
    },
    {
      label: 'Lora Signal Strength',
      value: gateway?.lora_signal_strength || '/',
      isVisible: true
    },
    {
      label: 'Lat',
      value: gateway?.lat || '/',
      isVisible: true
    },
    {
      label: 'Lng',
      value: gateway?.lng || '/',
      isVisible: true
    },
    {
      label: 'Created at',
      value: <>{(gateway?.created_at && dayjs(gateway?.created_at).format(DATE_FORMAT.DATETIME)) || '/'}</>,
      isVisible: true
    },
    {
      label: 'Updated at',
      value: <>{(gateway?.updated_at && dayjs(gateway?.updated_at).format(DATE_FORMAT.DATETIME)) || '/'}</>,
      isVisible: true
    },
    {
      label: 'Deleted at',
      value: <>{(gateway?.deleted_at && dayjs(gateway?.deleted_at).format(DATE_FORMAT.DATETIME)) || '/'}</>,
      isVisible: true
    }
  ];

  const initialFormValues = {
    identifier: gateway?.identifier,
    name: gateway?.name,
    appeui: gateway?.appeui,
    gweui: gateway?.gweui,
    arn: gateway?.arn,
    version: gateway?.version,
    carrier_type: gateway?.carrier_type
  };

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (submittable) {
          await saveGateway();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formEditRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        formEditRef.current?.resetFields();
        setIsFormDirty(false);
        onCancelEdit(false, 'editing');
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : onCancelEdit(false, 'editing');
  }, [isFormDirty]);

  return (
    <>
      {loading ? (
        <LoadingBar fullscreen={true} />
      ) : (
        <>
          {NavigationPrompt}
          <Flex gap="small" vertical={false} justify="space-between" wrap>
            <Flex vertical={true}>
              <Title level={4}>
                {state.editing && 'Edit '}Gateway {gateway?.name || gateway?.appeui}
              </Title>
              <AppBreadcrumb
                items={[
                  {
                    title: <Link to="/administration/devices?tab=gateways">List of Kraal Gateways</Link>
                  },
                  {
                    title: `${state.editing ? 'Edit Gateway ' : 'Gateway '}${gateway?.name || gateway?.appeui}`
                  }
                ]}
              />
            </Flex>
            <Flex gap="small">
              {!state.editing && !gateway?.deleted_at && <AppDropdown label="Actions" items={actions} />}
              {state.editing && (
                <CancelSaveButtons disabled={!submittable} handleCancel={handleCancel} handleSave={saveGateway} />
              )}
            </Flex>
          </Flex>

          <Card title={`Gateway ${gateway?.name || gateway?.appeui} Details`} className="mb-2">
            <AppForm
              data={data}
              formRef={formEditRef}
              initialValues={initialFormValues}
              isEditMode={state.editing}
              onChange={setIsFormDirty}
              setSubmittable={setSubmittable}
            />
          </Card>

          <AppModal
            isOpen={archiveModalVisible}
            confirmButtonColor="danger"
            confirmButtonText="Archive"
            title="Archive gateways"
            handleCancel={closeArchiveModal}
            handleConfirm={deleteGateways}>
            <div className="py-4">
              {gateway?.connected_tags_count
                ? 'This gateway has active tag connections. Archiving this gateway will disconnect all tags connected to this gateway. Are you sure you want to archive this gateway?'
                : messages.ARCHIVE_CONFIRMATION('selected gateways')}
            </div>
          </AppModal>

          <UnsavedChangesModal
            isOpen={modalProps.visible}
            handleCancel={modalProps.onCancel}
            handleConfirm={modalProps.onSave}
            handleDiscard={modalProps.onDiscard}
          />
        </>
      )}
    </>
  );
}

export default EditGateway;
